import { useEffect, useState } from 'react';
import { Factory } from 'lucide-react';
import type { CursorPage, Plant } from '@dru/types';
import { cn } from '@dru/ui';
import { api } from '../lib/api';

const KEY = 'dru.viewPlant';

/** The plant remembered on this browser, or null for all plants. */
export function storedViewPlant(): string | null {
  try { return localStorage.getItem(KEY) || null; } catch { return null; }
}
export function storeViewPlant(id: string | null) {
  try { if (id) localStorage.setItem(KEY, id); else localStorage.removeItem(KEY); } catch { /* private window */ }
}

/**
 * "Which plant am I looking at?" — for a company-wide login (owner, company
 * admin). A plant login never sees it: it is already held to its own plant.
 * One plant is always chosen (owner, 26 Oct 2026: one plant's data at a
 * time, never all together); with nothing remembered it is the main plant,
 * the company's first. Every read on every HR screen is narrowed to it, the
 * same way a plant login is narrowed.
 */
export function PlantSwitcher({ value, onChange, className }: {
  value: string | null; onChange: (id: string | null) => void; className?: string;
}) {
  const [plants, setPlants] = useState<Plant[] | null>(null);
  useEffect(() => {
    let live = true;
    api.getAllPlants<CursorPage<Plant>>('/plants?limit=100')
      .then((p) => {
        if (!live) return;
        const list = [...p.items].sort((a, b) => a.name.localeCompare(b.name));
        setPlants(list);
        // Nothing remembered, or a plant that is gone or not this company's:
        // the main plant — the oldest; the API lists newest first.
        if (!value || !list.some((x) => x.id === value)) {
          const main = p.items[p.items.length - 1];
          if (main) onChange(main.id);
        }
      })
      .catch(() => { if (live) setPlants([]); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One plant: nothing to switch between (and nothing narrowed: the whole
  // company is that plant).
  if (!plants || plants.length < 2) return null;
  return (
    <label className={cn('relative flex items-center', className)}>
      <span className="sr-only">Plant</span>
      <Factory aria-hidden className="pointer-events-none absolute left-2.5 h-4 w-4 text-neutral-500" />
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-9 max-w-[14rem] appearance-none truncate rounded-btn border bg-white pl-8 pr-7 text-small font-medium text-neutral-800',
          'focus:outline-none focus:ring-2 focus:ring-primary-100',
          'border-primary-500 bg-primary-50',
        )}
      >
        {!value && <option value="" disabled>Choose a plant</option>}
        {plants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <svg aria-hidden className="pointer-events-none absolute right-2 h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
    </label>
  );
}
